import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { termsStatusQueryKey } from 'hooks/useTermsStatus'
import { auth } from 'lib/firebase'
import { subscribeTermsGateRequired } from 'lib/termsGate'
import type { TermsGateCode } from 'lib/termsGate'
import type { TTermsStatus } from 'types/terms'

/** 業務APIの 403/503 を受けた時点で、取り直しを待たずに業務利用を止める状態 */
export const blockedTermsStatus = (
  code: TermsGateCode,
  previous: TTermsStatus | undefined,
): TTermsStatus => ({
  state: code === 'TERMS_UNAVAILABLE' ? 'UNAVAILABLE' : 'ACCEPTANCE_REQUIRED',
  consentRequired: previous?.consentRequired ?? true,
  businessAccessAllowed: false,
  notificationRegistrationAllowed: false,
  acceptanceAllowed: code !== 'TERMS_UNAVAILABLE',
  requiredVersion: previous?.requiredVersion ?? null,
})

/**
 * 業務APIが利用規約の同意を要求したら、その要求を出した本人（UID）の同意状態を即座に
 * 「業務不可」へ置き換え、RequireAuth に同意画面への遷移（業務画面・AppLayout・PubNub・
 * 通知登録の解除）をさせる。そのうえで最新の状態を取り直す。取り直しに失敗しても、
 * 古い「利用可」へは戻さない。
 */
export function TermsGateListener() {
  const queryClient = useQueryClient()

  useEffect(
    () =>
      subscribeTermsGateRequired(({ code, uid }) => {
        // ログアウト・別アカウントでのログイン後に届いた古い応答で、今の利用者を止めない
        if (uid !== auth.currentUser?.uid) return
        const queryKey = termsStatusQueryKey(uid)
        void (async () => {
          // 403 より前に始まった取得（古い「利用可」）で上書きされないよう先に取り消す
          await queryClient.cancelQueries({ queryKey })
          // 取り消しを待つ間に別の利用者へ切り替わっていたら、前の利用者の状態を作り直さない
          if (uid !== auth.currentUser?.uid) return
          queryClient.setQueryData<TTermsStatus>(queryKey, (previous) =>
            blockedTermsStatus(code, previous),
          )
          await queryClient.invalidateQueries({ queryKey })
        })()
      }),
    [queryClient],
  )

  return null
}
